import type { BannerTreatment } from "./divisions.js";
import { LEARN_MORE_HREF } from "./config.js";

/** Scoped stylesheet for the shadow root. `:host{all:initial}` is the isolation guarantee. */
function styles(t: BannerTreatment): string {
  return (
    // Shadow DOM isolation means we can't rely on the host page having Montserrat
    // loaded, so we self-load it. `display=swap` means this never blocks/delays
    // the banner's first paint - it renders in the fallback stack immediately and
    // swaps once the font loads (falls back permanently if the import is blocked).
    "@import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap');" +
    ":host{all:initial;display:block}" +
    "*{box-sizing:border-box}" +
    `.banner{background:${t.color};color:#fff;padding:14px;text-align:center;` +
    "font-family:Montserrat,system-ui,-apple-system,Segoe UI,Roboto,sans-serif;font-size:13px}" +
    ".banner a{color:#fff;font-weight:600;text-decoration:underline;text-underline-offset:2px;margin-left:6px}" +
    // Accessibility: the default focus ring can be low-contrast against a
    // colored background; force a visible white one instead.
    "a:focus-visible{outline:2px solid #fff;outline-offset:2px;border-radius:2px}"
  );
}

/**
 * Splits the plain-text copy into a normal/bold/normal run so the brand name
 * (or, for the "Our next chapter: ..." shape, the lead-in) renders bolded.
 * Falls back to fully plain, unbolded text if the copy doesn't match either
 * known shape. Covers all six division/variant copy combinations - see
 * this package's tests.
 */
export function parseCopy(text: string): { prefix: string; bold: string; suffix: string } {
  const withLead = text.match(/^(Our next chapter (?:with|as) )(.+)$/);
  if (withLead) {
    return { prefix: withLead[1], bold: withLead[2], suffix: "." };
  }
  const withColon = text.match(/^(Our next chapter:)\s*(.+)$/);
  if (withColon) {
    return { prefix: "", bold: withColon[1], suffix: ` ${withColon[2]}.` };
  }
  return { prefix: `${text}.`, bold: "", suffix: "" };
}

/** Build the shadow-root markup for a resolved treatment. Exported for unit testing. */
export function renderBannerHTML(treatment: BannerTreatment, href: string = LEARN_MORE_HREF): string {
  const { prefix, bold, suffix } = parseCopy(treatment.copy);
  const message = bold ? `${prefix}<strong>${bold}</strong>${suffix}` : prefix;
  return (
    `<style>${styles(treatment)}</style>` +
    `<div class="banner">${message}` +
    `<a href="${href}" target="_blank" rel="noopener noreferrer">Learn more &rarr;</a></div>`
  );
}

/**
 * <ahi-banner> — the standalone transition banner.
 *
 * - Renders entirely inside an open Shadow DOM (`:host{all:initial}`) so host-site
 *   CSS can neither affect it nor be affected by it.
 * - Takes an already-resolved `treatment` ({color, copy}) - division/variant
 *   resolution happens in bootstrap.ts, keeping this component agnostic of the
 *   division code system.
 * - `role="region"` (not `banner`) so it doesn't collide with a page's actual
 *   `<ahi-header>` or native `<header>` landmark if both are present.
 */
export class AhiBanner extends HTMLElement {
  private _root: ShadowRoot | null = null;
  private _treatment: BannerTreatment | null = null;

  connectedCallback(): void {
    if (!this.hasAttribute("role")) this.setAttribute("role", "region");
    if (!this.hasAttribute("aria-label")) this.setAttribute("aria-label", "Announcement");
    if (!this._root) this._root = this.attachShadow({ mode: "open" });
    if (this._treatment) this.render(this._treatment);
  }

  set treatment(value: BannerTreatment) {
    this._treatment = value;
    if (this._root) this.render(value);
  }

  get treatment(): BannerTreatment | null {
    return this._treatment;
  }

  render(treatment: BannerTreatment): void {
    if (!this._root) return;
    this._root.innerHTML = renderBannerHTML(treatment);
  }
}

/** Register the element once. Safe to call repeatedly (guards double-definition). */
export function defineBanner(): void {
  if (typeof customElements !== "undefined" && !customElements.get("ahi-banner")) {
    customElements.define("ahi-banner", AhiBanner);
  }
}
